import React, { useCallback, useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, ExternalLink, Download } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export const isImageFile = (f) => {
  const c = `${f?.content_type || ""} ${f?.name || ""}`.toLowerCase();
  return c.includes("image") || /\.(png|jpe?g|webp|gif|bmp|heic|heif|avif)$/.test(c);
};
export const imgSrc = (f) => f.src || `${BACKEND}${f.url}`;

/* Full-screen photo-album viewer: large image + Prev/Next + filmstrip + keyboard arrows */
export function ImageLightbox({ images, startIndex = 0, onClose }) {
  const [i, setI] = useState(startIndex);
  const n = images.length;
  const go = useCallback((d) => setI((x) => (x + d + n) % n), [n]);
  useEffect(() => {
    const h = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") go(1);
      else if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [go, onClose]);
  if (!n) return null;
  const cur = images[i];
  return (
    <div className="fixed inset-0 z-[95] bg-black/90 flex flex-col" data-testid="image-lightbox" onClick={onClose}>
      <div className="flex items-center justify-between px-4 py-2 text-white" onClick={(e) => e.stopPropagation()}>
        <span className="text-[13px] font-semibold truncate pr-3">{cur.name} <span className="text-white/60">· {i + 1} / {n}</span></span>
        <div className="flex items-center gap-3 shrink-0">
          <a href={imgSrc(cur)} target="_blank" rel="noreferrer" data-testid="lightbox-open-original" className="text-white/80 hover:text-white inline-flex items-center gap-1 text-[12px]">Open <ExternalLink className="h-3.5 w-3.5" /></a>
          <a href={imgSrc(cur)} download={cur.name} className="text-white/80 hover:text-white inline-flex items-center gap-1 text-[12px]"><Download className="h-3.5 w-3.5" /></a>
          <button onClick={onClose} data-testid="lightbox-close" className="hover:bg-white/15 rounded p-1"><X className="h-6 w-6" /></button>
        </div>
      </div>
      <div className="flex-1 flex items-center justify-center relative px-2 pb-2" onClick={(e) => e.stopPropagation()}>
        {n > 1 && <button onClick={() => go(-1)} data-testid="lightbox-prev" title="Previous (←)" className="absolute left-2 sm:left-6 text-white/90 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2"><ChevronLeft className="h-7 w-7" /></button>}
        <img src={imgSrc(cur)} alt={cur.name} className="max-h-[80vh] max-w-[92vw] object-contain rounded shadow-2xl" />
        {n > 1 && <button onClick={() => go(1)} data-testid="lightbox-next" title="Next (→)" className="absolute right-2 sm:right-6 text-white/90 hover:text-white bg-black/40 hover:bg-black/60 rounded-full p-2"><ChevronRight className="h-7 w-7" /></button>}
      </div>
      {n > 1 && (
        <div className="shrink-0 overflow-x-auto flex gap-1.5 px-4 pb-4 justify-start sm:justify-center" onClick={(e) => e.stopPropagation()}>
          {images.map((im, idx) => (
            <button key={im.id} onClick={() => setI(idx)} data-testid={`lightbox-thumb-${idx}`}
              className={`h-14 w-14 shrink-0 rounded overflow-hidden border-2 ${idx === i ? "border-sky-400" : "border-transparent opacity-60 hover:opacity-100"}`}>
              <img src={imgSrc(im)} alt="" loading="lazy" className="h-full w-full object-cover" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
